import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { LayoutGroup } from 'framer-motion';
import useAudio from '../../UseAudio';
import AnimatedCard from '../../common/AnimatedCard';
import AnimatedFaceDownHand from '../../common/AnimatedFaceDownHand';
import PlayerLabel from '../../common/PlayerLabel';

import Scoreboard from './ScoreBoard';
import GameStatus from './GameStatus';
import RoomControls from '../../common/RoomControls';
import BiddingPanel from './BiddingPanel';
import DoublingPanel from './DoublingPanel';
import TrumpSelector from './TrumpSelector';
import RoundOverPanel from './RoundOverPanel';
import MatchOverPanel from './MatchOverPanel';
import TrickArea from './TrickArea';
import RoyalPairOverlay from './RoyalPairOverlay';
import TrumpRevealOverlay from './TrumpRevealOverlay';

import '../../common/GameBoard.css';
import '../../common/Cards.css';
import './Overlays.css';

function getRelPos(diff, total) {
  if (total === 4) {
    if (diff === 0) return 'bottom';
    if (diff === 1) return 'right';
    if (diff === 2) return 'top';
    if (diff === 3) return 'left';
  }
  if (total === 6) {
    if (diff === 0) return 'bottom';
    if (diff === 1) return 'bottom-right';
    if (diff === 2) return 'top-right';
    if (diff === 3) return 'top';
    if (diff === 4) return 'top-left';
    if (diff === 5) return 'bottom-left';
  }
  return 'hidden';
}

const getSeatStyle = (relPos) => {
  const base = { position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 5 };
  switch (relPos) {
    case 'bottom':       return { ...base, bottom: '2%', left: '50%', transform: 'translateX(-50%)', zIndex: 10 };
    case 'top':          return { ...base, top: '2%', left: '50%', transform: 'translateX(-50%)' };
    case 'left':         return { ...base, left: '2%', top: '50%', transform: 'translateY(-50%)', justifyContent: 'center' };
    case 'right':        return { ...base, right: '2%', top: '50%', transform: 'translateY(-50%)', justifyContent: 'center' };
    case 'top-left':     return { ...base, top: '10%', left: '15%' };
    case 'top-right':    return { ...base, top: '10%', right: '15%' };
    case 'bottom-left':  return { ...base, bottom: '15%', left: '15%' };
    case 'bottom-right': return { ...base, bottom: '15%', right: '15%' };
    default:             return { display: 'none' };
  }
};

export default function Board28({ gameState: gs, myPlayerId: truePlayerId, send, error, trickNotif, clearError, onLeave, isSpectator }) {
  const { playSound } = useAudio();
  
  const prevTotalCards = useRef(0);
  const prevPhase = useRef(null); 
  const prevTrumpRevealed = useRef(null);
  const wasMyTurn = useRef(false);

  const players = gs.players || [];
  const totalPlayers = gs.mode === '56' ? 6 : 4;
  
  // SPECTATOR PERSPECTIVE: If isSpectator, myId is -1 but we anchor the view to seat 0
  const myId = isSpectator ? 0 : (gs.your_index !== undefined ? gs.your_index : players.findIndex(p => p && p.id === truePlayerId));
  
  const myHand = gs.your_hand || [];
  const validPlays = useMemo(() => {
    return gs?.valid_plays || [];
    }, [gs?.valid_plays]);  
  const facedownCard = gs.facedown_card || null;

  const [delayedOverlay, setDelayedOverlay] = useState(false);
  const [localAnnouncementActive, setLocalAnnouncementActive] = useState(gs.announcement_active);

  useEffect(() => {
    setLocalAnnouncementActive(gs.announcement_active);
    if (gs.announcement_active && gs.block_actions_until && gs.server_time) {
      const remaining = gs.block_actions_until - gs.server_time;
      if (remaining > 0) {
        const timer = setTimeout(() => {
          setLocalAnnouncementActive(false);
        }, remaining + 100);
        return () => clearTimeout(timer);
      } else {
        setLocalAnnouncementActive(false);
      }
    }
  }, [gs.announcement_active, gs.block_actions_until, gs.server_time]);

  useEffect(() => {
    const totalInHands = gs.hand_counts?.reduce((acc, count) => acc + (count || 0), 0) || 0;
    
    const cardWasPlayed = prevTotalCards.current > 0 && totalInHands < prevTotalCards.current;
    if (cardWasPlayed && (gs.phase === 'playing' || prevPhase.current === 'playing')) {
      playSound('cardPlay');
    }
    prevTotalCards.current = totalInHands;

    if (gs.phase === 'bidding' && prevPhase.current !== 'bidding') {
        if (prevPhase.current === null) {
          playSound('startGame');
        }
        playSound('cardShuffle');
    }

    if (gs.trump_revealed && prevTrumpRevealed.current === false) {
      playSound('trumpReveal');
    }
    prevTrumpRevealed.current = gs.trump_revealed;

    const isPlayingTurn = gs.phase === 'playing' && gs.current_turn === myId;
    const isBiddingTurn = gs.phase === 'bidding' && gs.bid_turn === myId;
    const isTrumpTurn = gs.phase === 'trump_selection' && gs.bid_winner === myId;
    const isDoublingTurn = gs.phase === 'doubling' && (myId % 2 !== gs.bid_winner % 2) && !gs.doubling_passed?.[myId];
    const isRedoublingTurn = gs.phase === 'redoubling' && (myId % 2 === gs.bid_winner % 2) && !gs.redoubling_passed?.[myId];

    const isMyTurnNow = !isSpectator && (isPlayingTurn || isBiddingTurn || isTrumpTurn || isDoublingTurn || isRedoublingTurn);

    if (isMyTurnNow && !wasMyTurn.current) {
      setTimeout(() => {
        playSound('yourTurn');
      }, 500); 
    }
    wasMyTurn.current = isMyTurnNow;
    prevPhase.current = gs.phase;

  }, [gs, myId, playSound, isSpectator]);

  useEffect(() => {
    if (gs.phase === 'round_over' || gs.phase === 'match_over') {
      const timer = setTimeout(() => {
        setDelayedOverlay(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setDelayedOverlay(false);
    }
  }, [gs.phase]);
  
  const isMyTurn = !isSpectator && gs.phase === 'playing' && gs.current_turn === myId;
  const ledSuit = gs.current_trick && gs.current_trick.length > 0 ? gs.current_trick[0].card.suit : null;
  const canFollow = ledSuit && validPlays.some(vc => vc.suit === ledSuit);
  const canRevealTrump = !isSpectator && isMyTurn && gs.current_trick && gs.current_trick.length > 0 && !canFollow && !gs.trump_revealed;

  const isPlayable = useCallback((card) => {
    return !isSpectator && validPlays.some(vc => vc.suit === card.suit && vc.rank === card.rank);
  }, [validPlays, isSpectator]);

  const suitOrder = { S: 0, H: 1, C: 2, D: 3 };
  const ro = { '7': 0, '8': 1, 'Q': 2, 'K': 3, '10': 4, 'A': 5, '9': 6, 'J': 7 };

  const sortedHand = myHand
    .map((c, i) => ({ ...c, serverIdx: i }))
    .sort((a, b) => {
      if (a.suit !== b.suit) return suitOrder[a.suit] - suitOrder[b.suit];
      if (a.rank !== b.rank) return ro[a.rank] - ro[b.rank];
      return a.serverIdx - b.serverIdx;
    });

  const showPanel = 
    !isSpectator && (
      ['bidding', 'doubling', 'redoubling', 'trump_selection'].includes(gs.phase) || 
      (['round_over', 'match_over'].includes(gs.phase) && delayedOverlay)
    );

  const opponents = [];
  // ... (no changes to opponents logic)
  for (let i = 1; i < totalPlayers; i++) {
    const oppId = (myId + i) % totalPlayers;
    const relPos = getRelPos(i, totalPlayers);
    const isVertical = relPos.includes('left') || relPos.includes('right');
    
    opponents.push(
      <div key={oppId} style={getSeatStyle(relPos)}>
        <PlayerLabel 
          player={players[oppId]} 
          playerId={oppId} 
          gs={gs} 
          myId={isSpectator ? -1 : myId} 
          send={send} 
          isSpectator={isSpectator}
        />
        <div style={{ marginTop: '8px' }}>
          <AnimatedFaceDownHand 
            count={gs.hand_counts?.[oppId]} 
            vertical={isVertical} 
            playerIndex={i}
            myId={myId}
          />
        </div>
      </div>
    );
  }

  return (
    <LayoutGroup>
      <div 
        className={`game-board players-${totalPlayers} ${isSpectator ? 'spectator-view' : ''}`} 
        style={{ 
          width: '100vw', 
          height: '100vh', 
          position: 'relative', 
          overflow: 'hidden',
          pointerEvents: localAnnouncementActive ? 'none' : 'auto'
        }}
      >
        {isSpectator && (
          <div style={{
            position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, background: 'rgba(59, 130, 246, 0.8)', color: 'white',
            padding: '8px 20px', borderRadius: '20px', fontWeight: 'bold',
            backdropFilter: 'blur(10px)', border: '1px solid #3b82f6',
            pointerEvents: 'none'
          }}>
            👁 SPECTATING
          </div>
        )}

        <RoomControls onLeave={onLeave} gs={gs} myId={truePlayerId} send={send} />
        
        <div style={{ position: 'absolute', top: totalPlayers === 6 ? 120 : 16, right: 16, zIndex: 10 }}>
          <Scoreboard gs={gs} myId={myId} />
        </div>
        
        <div style={{ position: 'absolute', bottom: totalPlayers === 6 ? 140 : 24, right: 24, zIndex: 10 }}>
          <GameStatus gs={gs} />
        </div>

        {opponents}

        <div style={{
            position: 'absolute', 
            top: totalPlayers === 6 ? '40%' : '44%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '400px', 
            height: '400px'
        }}>
          <TrickArea gs={gs} myId={myId} totalPlayers={totalPlayers} trickNotif={trickNotif} />
        </div>

        <div style={getSeatStyle('bottom')}>
          <div className="my-hand">
            {isSpectator ? (
              <AnimatedFaceDownHand count={gs.hand_counts?.[myId]} playerIndex={0} myId={myId} />
            ) : (
              sortedHand.map((card, idx) => {
                const isTheActualTrump = facedownCard && card.serverIdx === myHand.length - 1;
                const cardId = `my-card-${card.serverIdx}-${card.suit}-${card.rank}`;

                return (
                  <AnimatedCard
                    key={cardId}
                    card={card}
                    layoutId={cardId}
                    className="card-wrapper"
                    playable={isMyTurn && isPlayable(card)}
                    faceDown={Boolean(isTheActualTrump && !gs.trump_revealed)} 
                    onClick={isMyTurn && isPlayable(card) ? () => send('play_card', card) : null}
                    zIndexProp={idx + 10} 
                  />
                );
              })
            )}
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '8px' }}>
            <PlayerLabel 
              player={players[myId]} 
              playerId={myId} 
              gs={gs} 
              myId={isSpectator ? -1 : myId} 
              send={send} 
              isSpectator={isSpectator}
            />
            {!isSpectator && isMyTurn && gs.phase === 'playing' && <div className="your-turn-hint" style={{ marginTop: '6px' }}>✨ Your turn</div>}
            {!isSpectator && canRevealTrump && <button className="btn btn-secondary" onClick={() => send('reveal_trump', {})} style={{ marginTop: '6px' }}>Reveal Trump</button>}
          </div>
        </div>

        {showPanel && (
          <div className="phase-overlay" style={{ 
            zIndex: 9999, 
            paddingBottom: totalPlayers === 6 ? '12vh' : '0' 
          }}>
            {gs.phase === 'bidding' && <BiddingPanel gs={gs} myId={myId} send={send} />}
            {['doubling', 'redoubling'].includes(gs.phase) && <DoublingPanel gs={gs} myId={myId} send={send} />}
            {gs.phase === 'trump_selection' && <TrumpSelector gs={gs} myId={myId} send={send} />}
            {gs.phase === 'round_over' && <RoundOverPanel gs={gs} myId={myId} send={send} />}
            {gs.phase === 'match_over' && <MatchOverPanel gs={gs} myId={myId} send={send} />}
          </div>
        )}

        <RoyalPairOverlay gs={gs} active={localAnnouncementActive} />
        <TrumpRevealOverlay gs={gs} active={localAnnouncementActive} />

        {error && <div className="error-toast" onClick={clearError}>⚠ {error}</div>}
        {trickNotif && <div className="trick-notif">🏆 {players[trickNotif.winner_id]?.name || `Player ${trickNotif.winner_id}`} won the trick!</div>}

      </div>
    </LayoutGroup>
  );
}